"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { Calculator, Gauge, SlidersHorizontal, Table as TableIcon, ShieldAlert, ArrowRight, Maximize2, CircleCheck } from "lucide-react";
interface MeshReferenceRow {
  mesh: number;
  wireDiameterMm: number;
  apertureMm: number;
  apertureMicron: number;
  openAreaPct: number;
  nominalWeightKgM2: number;
}
const REFERENCE_DATA: MeshReferenceRow[] = [{
  mesh: 10,
  wireDiameterMm: 0.5,
  apertureMm: 2.04,
  apertureMicron: 2040,
  openAreaPct: 64.5,
  nominalWeightKgM2: 1.25
}, {
  mesh: 20,
  wireDiameterMm: 0.4,
  apertureMm: 0.87,
  apertureMicron: 870,
  openAreaPct: 46.9,
  nominalWeightKgM2: 1.6
}, {
  mesh: 50,
  wireDiameterMm: 0.2,
  apertureMm: 0.308,
  apertureMicron: 308,
  openAreaPct: 36.8,
  nominalWeightKgM2: 1.0
}, {
  mesh: 100,
  wireDiameterMm: 0.1,
  apertureMm: 0.154,
  apertureMicron: 154,
  openAreaPct: 36.8,
  nominalWeightKgM2: 0.5
}, {
  mesh: 200,
  wireDiameterMm: 0.05,
  apertureMm: 0.077,
  apertureMicron: 77,
  openAreaPct: 36.8,
  nominalWeightKgM2: 0.25
}, {
  mesh: 325,
  wireDiameterMm: 0.035,
  apertureMm: 0.043,
  apertureMicron: 43,
  openAreaPct: 30.5,
  nominalWeightKgM2: 0.2
}];
interface GuideCopy {
  eyebrow: string;
  heading: string;
  lead: string;
  card1Title: string;
  card1Desc: string;
  card1Body: string;
  card1MetrologyHeading: string;
  card1PointMicrometerTitle: string;
  card1PointMicrometerDesc: string;
  card1OpticalTitle: string;
  card1OpticalDesc: string;
  card1LaserTitle: string;
  card1LaserDesc: string;
  card2Title: string;
  card2Desc: string;
  card2Body: string;
  card2FormulaLabel: string;
  card2VarW: string;
  card2VarD: string;
  card2VarPitch: string;
  card2VarAo: string;
  card2Note: string;
  tradeoffTitle: string;
  tradeoffDesc: string;
  tradeoffBadge: string;
  thickerTitle: string;
  thickerBadge: string;
  thickerDesc: string;
  thickerRow1Label: string;
  thickerRow1Val: string;
  thickerRow2Label: string;
  thickerRow2Val: string;
  thickerRow3Label: string;
  thickerRow3Val: string;
  thickerRow4Label: string;
  thickerRow4Val: string;
  thickerAlert: string;
  thinnerTitle: string;
  thinnerBadge: string;
  thinnerDesc: string;
  thinnerRow1Label: string;
  thinnerRow1Val: string;
  thinnerRow2Label: string;
  thinnerRow2Val: string;
  thinnerRow3Label: string;
  thinnerRow3Val: string;
  thinnerRow4Label: string;
  thinnerRow4Val: string;
  thinnerCheck: string;
  tableTitle: string;
  tableDesc: string;
  viewAllCharts: string;
  thMesh: string;
  thWireDia: string;
  thApertureMm: string;
  thApertureMicron: string;
  thOpenArea: string;
  thWeight: string;
  thApplication: string;
  app10: string;
  app20: string;
  app50: string;
  app100: string;
  app200: string;
  app325: string;
  footerNote: string;
  deltaPModels: string;
  technicalResources: string;
}
const copyData: Record<Lang, GuideCopy> = {
  en: {
    eyebrow: "Engineering Calculations & Standards",
    heading: "Core Principles of Wire Diameter & Open Area",
    lead: "In precision woven industrial wire mesh, wire diameter (d) and aperture width (w) directly govern open area ratio (Ao), dictating the critical engineering trade-off between mechanical strength, pressure drop, and volumetric throughput.",
    card1Title: "1. Wire Diameter (d) & Measurement",
    card1Desc: "Nominal calibration, crimping compensation, and ASTM E2016 verification",
    card1Body: "Wire diameter (d) is the nominal circular cross-sectional thickness of raw warp and weft wires prior to or within the woven fabric. During the weaving process, bending and interlocking at intersections create micro-flattening (crimp), requiring standardized measurement techniques.",
    card1MetrologyHeading: "Standardized Metrology",
    card1PointMicrometerTitle: "Ball-Point Micrometer:",
    card1PointMicrometerDesc: "Measures straight unknitted wire sections, avoiding distortion from weave knuckles.",
    card1OpticalTitle: "Optical Projection Profile:",
    card1OpticalDesc: "Non-contact calibrated digital projection for fine meshes (≤0.10 mm) preventing deformation.",
    card1LaserTitle: "Laser Scanning Metrology:",
    card1LaserDesc: "Continuous in-line laser monitoring ensuring batch wire gauge tolerances conform to ASTM E2016.",
    card2Title: "2. Open Area (Ao) & Formula",
    card2Desc: "Theoretical open screening ratio for square plain-weave mesh",
    card2Body: "Open area (Ao) represents the proportion of geometric net apertures relative to the total envelope area of the screen mesh. It is the fundamental factor in determining volumetric throughput and initial differential pressure (ΔP).",
    card2FormulaLabel: "Geometric Open Area Equation (Square Mesh)",
    card2VarW: "Aperture width (mm)",
    card2VarD: "Wire diameter (mm)",
    card2VarPitch: "Weave pitch (25.4 / Mesh)",
    card2VarAo: "Open area percentage (%)",
    card2Note: "Note: For Dutch weave (twilled/plain Dutch cloth), adjacent wires are pressed together with near-zero line-of-sight open area; filtration rating is characterized via bubble point pore sizing rather than projection Ao.",
    tradeoffTitle: "3. Engineering Trade-off: Thicker vs. Thinner Wire",
    tradeoffDesc: "Balancing structural resistance against flow capacity and fluid drag",
    tradeoffBadge: "Same Aperture Selection",
    thickerTitle: "Heavy / Thicker Wire",
    thickerBadge: "High Rigidity",
    thickerDesc: "Recommended for high differential pressure surges, abrasive slurries, heavy vibrating screens, and sintered mesh support backings.",
    thickerRow1Label: "Mechanical Strength & Burst",
    thickerRow1Val: "Significantly Superior",
    thickerRow2Label: "Open Area Percentage (Ao)",
    thickerRow2Val: "Reduced (Lower Flow)",
    thickerRow3Label: "Initial Clean Pressure Drop",
    thickerRow3Val: "Higher Friction Loss",
    thickerRow4Label: "Wear & Fatigue Life",
    thickerRow4Val: "Long Cycle Under Load",
    thickerAlert: "Requires higher pump horsepower to offset line loss from reduced passage area.",
    thinnerTitle: "Light / Thinner Wire",
    thinnerBadge: "High Permeability",
    thinnerDesc: "Ideal for gravity separators, air/intake filters, viscous liquid de-dusting, and pleated filter elements requiring maximum free area.",
    thinnerRow1Label: "Open Area Percentage (Ao)",
    thinnerRow1Val: "Maximised (High Flow)",
    thinnerRow2Label: "Initial Clean Pressure Drop",
    thinnerRow2Val: "Minimal Resistance",
    thinnerRow3Label: "Mechanical Self-Support",
    thinnerRow3Val: "Low (Needs Perforated Core)",
    thinnerRow4Label: "Cake Release & Backwash",
    thinnerRow4Val: "Clean Hydraulic Detachment",
    thinnerCheck: "Frequently combined with perforated backer sleeves or multilayer sintering.",
    tableTitle: "4. Standard Mesh Open Area Reference Table",
    tableDesc: "Calibrated to ASTM E2016 plain-weave square mesh using exact formula Ao = (w / (w + d))² × 100",
    viewAllCharts: "View All Charts",
    thMesh: "Mesh Count",
    thWireDia: "Wire Dia. d (mm)",
    thApertureMm: "Aperture w (mm)",
    thApertureMicron: "Aperture (µm)",
    thOpenArea: "Open Area Ao (%)",
    thWeight: "Weight (~kg/m²)",
    thApplication: "Primary Application",
    app10: "Heavy aggregate scalping, coarse protective baskets",
    app20: "Fluidized bed support, petrochemical strainers",
    app50: "Water intake clarification, sand separation",
    app100: "Polymer extrusion screen packs, fuel line straining",
    app200: "Pharmaceutical powder classification, hydraulic oil",
    app325: "Ultra-fine chemical slurries, high-purity gas filters",
    footerNote: "MutualFilter supplies precision stainless steel 304, 304L, 316, and 316L wire meshes. Custom wire gauges and calendered weaves are engineered to match target pressure drops. Certified QA inspection records and material reports are available when specified in the order.",
    deltaPModels: "Delta P Models",
    technicalResources: "Technical Resources"
  },
  zh: {
    eyebrow: "工程计算与标准规范",
    heading: "丝径与开孔率核心工程原理",
    lead: "在工业编织金属丝网中，丝径（d）与孔径（w）共同决定了网孔的几何开孔率（Ao），并在结构强度、通过阻力与使用寿命之间形成核心工程权衡。",
    card1Title: "1. 丝径（d）定义与测量规范",
    card1Desc: "公称尺寸、编织压扁补偿与行业检验标准",
    card1Body: "丝径（d）指编织前或编织后单根金属经丝或纬丝的横截面外直径。在实际编织网片中，经纬金属丝在交叉咬合处会发生微小塑性形变与压扁，因此标准测量需区分光丝公称直径与织网实测直径。",
    card1MetrologyHeading: "标准检测方法",
    card1PointMicrometerTitle: "千分尺点测 (Point Micrometer):",
    card1PointMicrometerDesc: "采用球头或尖头千分尺单根丝多次测量取平均值，规避交叉点弯曲误差。",
    card1OpticalTitle: "光学影像比对仪 (Optical Comparator):",
    card1OpticalDesc: "高倍无接触投影测量，适合 100 目以上细丝（≤0.10 mm）无应力变形检测。",
    card1LaserTitle: "激光测径扫描 (Laser Micrometer):",
    card1LaserDesc: "在线连续光学测径监测，确保批次丝径公差符合 ASTM E2016 规范。",
    card2Title: "2. 开孔率（Ao）定义与公式计算",
    card2Desc: "平纹方孔筛网几何有效流通面积占比",
    card2Body: "开孔率（Open Area / Ao）指网面上所有贯通孔眼总面积占网片总表面积的百分比，直接决定介质的流速、通量及初级压降。",
    card2FormulaLabel: "标准几何开孔率公式 (Plain Weave)",
    card2VarW: "网孔宽度 / 孔径 (mm)",
    card2VarD: "金属丝径 (mm)",
    card2VarPitch: "网节距 / 节拍 (Pitch, mm)",
    card2VarAo: "开孔百分比 (%)",
    card2Note: "注意：对于密纹网（荷兰编织），经纬丝相互紧密靠拢，几何投影开孔率趋近于零，需通过气泡点法或微米绝对过滤精度进行流体力学换算。",
    tradeoffTitle: "3. 工程权衡：粗丝与细丝对比分析",
    tradeoffDesc: "机械抗冲压强度、有效流量与压降之间的系统平衡",
    tradeoffBadge: "同孔径设计选型",
    thickerTitle: "选择粗丝径 (Thicker Wire)",
    thickerBadge: "高强度型",
    thickerDesc: "当工况面临高压差脉冲、固相颗粒机械冲刷或作为烧结复合网的骨架支撑层时选用。",
    thickerRow1Label: "机械强度 / 抗拉破裂",
    thickerRow1Val: "显著提高 (Very High)",
    thickerRow2Label: "有效开孔率 (Ao)",
    thickerRow2Val: "降低 (Lower)",
    thickerRow3Label: "初始压降 (ΔP)",
    thickerRow3Val: "相对较高 (Higher)",
    thickerRow4Label: "耐磨损与变形寿命",
    thickerRow4Val: "长使用周期 (Extended)",
    thickerAlert: "需要更大泵送功率以弥补孔隙收窄带来的沿程水头损失。",
    thinnerTitle: "选择细丝径 (Thinner Wire)",
    thinnerBadge: "高通量型",
    thinnerDesc: "适用于重力筛分、低压降气体过滤器、高粘度流体脱杂以及轻量化过滤滤筒。",
    thinnerRow1Label: "有效开孔率 (Ao)",
    thinnerRow1Val: "显著增加 (Maximised)",
    thinnerRow2Label: "初始压降 (ΔP)",
    thinnerRow2Val: "极低压降 (Minimal)",
    thinnerRow3Label: "机械自支撑性",
    thinnerRow3Val: "较弱 (Requires Support)",
    thinnerRow4Label: "滤饼清除与反吹",
    thinnerRow4Val: "流动顺畅易剥离",
    thinnerCheck: "通常配合外层穿孔板骨架或多层复合烧结，以兼得高通量与抗压能力。",
    tableTitle: "4. 常用平纹筛网开孔率参考表",
    tableDesc: "基于标准 ASTM E2016 规范平纹编织计算：Ao = (w / (w + d))² × 100",
    viewAllCharts: "查看全部规格图表",
    thMesh: "目数 (Mesh)",
    thWireDia: "丝径 d (mm)",
    thApertureMm: "孔径 w (mm)",
    thApertureMicron: "微米标称 (µm)",
    thOpenArea: "开孔率 Ao (%)",
    thWeight: "理论克重 (kg/m²)",
    thApplication: "典型工业应用",
    app10: "重型骨料预筛、粗滤防护篮",
    app20: "流化床承托层、石化进料滤网",
    app50: "工业水预过滤、细砂颗粒筛分",
    app100: "聚合物挤出滤网片、燃油精密过滤",
    app200: "制药精细粉体筛分、航空液压油滤",
    app325: "超细化工悬浮液分离、高洁净气体净化",
    footerNote: "MutualFilter 生产全规格 304、304L、316、316L 不锈钢编织网，支持根据用户指定流速压降与物料磨损特性定制线径与开孔比例。可按订单要求提供实测尺寸检验记录与材质质检报告。",
    deltaPModels: "压降计算模型",
    technicalResources: "技术资源中心"
  },
  es: {
    eyebrow: "Cálculos de ingeniería y normas",
    heading: "Principios fundamentales de diámetro de hilo y área abierta",
    lead: "En la malla de alambre tejida industrial, el diámetro del hilo (d) y el tamaño de la abertura (w) determinan directamente la proporción de área abierta (Ao), definiendo el equilibrio crítico entre resistencia mecánica, pérdida de carga y caudal volumétrico.",
    card1Title: "1. Diámetro del hilo (d) y metrología",
    card1Desc: "Calibración nominal, compensación de ondulación y verificación ASTM E2016",
    card1Body: "El diámetro del hilo (d) es el espesor nominal de la sección circular del alambre de urdimbre y trama antes de tejer o dentro de la tela. Durante el tejido, el plegado en las intersecciones genera microaplanamiento, lo que exige métodos normalizados de medición.",
    card1MetrologyHeading: "Métodos de medición normalizados",
    card1PointMicrometerTitle: "Micrómetro de punta esférica:",
    card1PointMicrometerDesc: "Mide secciones rectas de alambre evitando distorsiones por ondulaciones del tejido.",
    card1OpticalTitle: "Proyector de perfiles óptico:",
    card1OpticalDesc: "Proyección digital sin contacto para mallas finas (≤0.10 mm) sin deformación mecánica.",
    card1LaserTitle: "Metrología de escaneo láser:",
    card1LaserDesc: "Control continuo en línea que asegura tolerancias de calibre de alambre según ASTM E2016.",
    card2Title: "2. Área abierta (Ao) y cálculo de fórmula",
    card2Desc: "Proporción teórica de paso libre para malla tejida cuadrada lisa",
    card2Body: "El área abierta (Ao) representa la proporción geométrica de aberturas netas respecto al área total de la malla. Es el factor determinante del caudal volumétrico y de la pérdida de carga diferencial inicial (ΔP).",
    card2FormulaLabel: "Fórmula de área abierta geométrica (malla cuadrada)",
    card2VarW: "Ancho de abertura (mm)",
    card2VarD: "Diámetro de hilo (mm)",
    card2VarPitch: "Paso de tejido (25.4 / Mesh)",
    card2VarAo: "Porcentaje de área abierta (%)",
    card2Note: "Nota: Para tejidos holandeses (Dutch weave), los alambres contiguos se tocan con área visual casi nula; el grado de filtración se mide por punto de burbuja y retención en micras.",
    tradeoffTitle: "3. Evaluación de ingeniería: hilo grueso vs. hilo fino",
    tradeoffDesc: "Equilibrio estructural entre resistencia a la presión, caudal y pérdida de carga",
    tradeoffBadge: "Selección para igual apertura",
    thickerTitle: "Alambre grueso (Thicker Wire)",
    thickerBadge: "Alta rigidez",
    thickerDesc: "Recomendado para fluctuaciones altas de presión, fluidos abrasivos, cribado vibratorio pesado y soporte de mallas sinterizadas.",
    thickerRow1Label: "Resistencia mecánica y rotura",
    thickerRow1Val: "Muy superior",
    thickerRow2Label: "Porcentaje de área abierta (Ao)",
    thickerRow2Val: "Reducida (menor caudal)",
    thickerRow3Label: "Pérdida de carga inicial (ΔP)",
    thickerRow3Val: "Mayor resistencia por fricción",
    thickerRow4Label: "Vida útil ante fatiga y desgaste",
    thickerRow4Val: "Ciclo extendido bajo carga",
    thickerAlert: "Requiere mayor potencia de bombeo para compensar la pérdida de carga en conductos reducidos.",
    thinnerTitle: "Alambre fino (Thinner Wire)",
    thinnerBadge: "Alta permeabilidad",
    thinnerDesc: "Ideal para separadores por gravedad, filtros de aspiración de aire, desarenado de fluidos viscosos y cartuchos plisados.",
    thinnerRow1Label: "Porcentaje de área abierta (Ao)",
    thinnerRow1Val: "Maximizada (alto caudal)",
    thinnerRow2Label: "Pérdida de carga inicial (ΔP)",
    thinnerRow2Val: "Resistencia mínima",
    thinnerRow3Label: "Autoportancia mecánica",
    thinnerRow3Val: "Baja (requiere núcleo perforado)",
    thinnerRow4Label: "Desprendimiento de torta y retrolavado",
    thinnerRow4Val: "Separación hidráulica limpia",
    thinnerCheck: "Se combina frecuentemente con tubos perforados de soporte o sinterización multicapa.",
    tableTitle: "4. Tabla de referencia de área abierta para mallas estándar",
    tableDesc: "Calibrada según la norma ASTM E2016 para malla cuadrada lisa: Ao = (w / (w + d))² × 100",
    viewAllCharts: "Ver todas las tablas",
    thMesh: "Número de malla",
    thWireDia: "Diám. hilo d (mm)",
    thApertureMm: "Apertura w (mm)",
    thApertureMicron: "Apertura (µm)",
    thOpenArea: "Área abierta Ao (%)",
    thWeight: "Peso (~kg/m²)",
    thApplication: "Aplicación principal",
    app10: "Cribado de áridos pesados, cestas de protección gruesa",
    app20: "Lecho fluidizado, filtros para petroquímica",
    app50: "Clarificación de toma de agua, separación de arenas",
    app100: "Discos de extrusión de polímeros, filtración de combustible",
    app200: "Clasificación de polvos farmacéuticos, aceite hidráulico",
    app325: "Suspensiones químicas ultrafinas, purificación de gases",
    footerNote: "MutualFilter suministra mallas de alambre de acero inoxidable 304, 304L, 316 y 316L de precisión. Se adaptan diámetros y tejidos calandrados para caídas de presión específicas. Informes de inspección de control de calidad y certificados de material disponibles cuando se especifiquen en el pedido.",
    deltaPModels: "Modelos Delta P",
    technicalResources: "Recursos técnicos"
  },
  de: {
    eyebrow: "Konstruktionsberechnungen & Normen",
    heading: "Grundprinzipien von Drahtdurchmesser & freier Siebfläche",
    lead: "Bei industriellem Drahtgewebe bestimmen Drahtdurchmesser (d) und Maschenweite (w) direkt den Anteil der offenen Fläche (Ao) und damit das konstruktive Gleichgewicht zwischen Festigkeit, Druckverlust und Durchsatz.",
    card1Title: "1. Drahtdurchmesser (d) und Messtechnik",
    card1Desc: "Nenndurchmesser, Kröpfungskompensation und Prüfung nach ASTM E2016",
    card1Body: "Der Drahtdurchmesser (d) bezeichnet die Querschnittsstärke der Kett- und Schussdrähte vor oder im Gewebe. Beim Webprozess entstehen an den Kreuzungspunkten Verformungen (Kröpfung), die genormte Messverfahren erfordern.",
    card1MetrologyHeading: "Genormte Messverfahren",
    card1PointMicrometerTitle: "Kugel-Mikrometer:",
    card1PointMicrometerDesc: "Misst gerade Drahtabschnitte und verhindert Messfehler an den Kröpfungsknoten.",
    card1OpticalTitle: "Optischer Profilprojektor:",
    card1OpticalDesc: "Berührungslose digitale Projektion für Feingewebe (≤0.10 mm) ohne Verformungsrisiko.",
    card1LaserTitle: "Laser-Scanning-Messtechnik:",
    card1LaserDesc: "Kontinuierliche In-Line-Lasermessung zur Einhaltung der Drahttoleranzen nach ASTM E2016.",
    card2Title: "2. Freie Siebfläche (Ao) und Formelberechnung",
    card2Desc: "Theoretischer freier Durchgangsanteil für glattes Quadratmaschengewebe",
    card2Body: "Die freie Siebfläche (Ao) bezeichnet den prozentualen Anteil der lichten Öffnungen an der Gesamtoberfläche. Sie ist die entscheidende Kenngröße für Durchsatzleistung und sauberen Differenzdruck (ΔP).",
    card2FormulaLabel: "Geometrische Gleichung der offenen Fläche (Quadratmasche)",
    card2VarW: "Maschenweite (mm)",
    card2VarD: "Drahtdurchmesser (mm)",
    card2VarPitch: "Teilung / Pitch (25.4 / Mesh)",
    card2VarAo: "Anteil offener Fläche (%)",
    card2Note: "Hinweis: Bei Tressengeweben liegen die Drähte dicht aneinander ohne direkte Sichtöffnung; die Filterfeinheit wird über den Blaspunkt oder die Mikrometerfeinheit bestimmt.",
    tradeoffTitle: "3. Konstruktionsabwägung: Dicker vs. dünner Draht",
    tradeoffDesc: "Abstimmung mechanischer Festigkeit gegen Durchsatzkapazität und Strömungswiderstand",
    tradeoffBadge: "Gleiche Maschenweite",
    thickerTitle: "Dicker Draht (Heavy Wire)",
    thickerBadge: "Hohe Stabilität",
    thickerDesc: "Empfohlen bei hohen Druckstößen, abrasiven Schlämmen, schweren Vibrationssieben und Stützlagen von Sintergeweben.",
    thickerRow1Label: "Mechanische Festigkeit & Berstdruck",
    thickerRow1Val: "Deutlich überlegen",
    thickerRow2Label: "Freie Siebfläche (Ao)",
    thickerRow2Val: "Reduziert (geringerer Durchfluss)",
    thickerRow3Label: "Anfänglicher Differenzdruck (ΔP)",
    thickerRow3Val: "Höherer Reibungsverlust",
    thickerRow4Label: "Verschleiß- & Dauerfestigkeit",
    thickerRow4Val: "Verlängerte Standzeit unter Last",
    thickerAlert: "Erfordert höhere Pumpenleistung zum Ausgleich des Druckverlusts bei verkleinerter Durchtrittsfläche.",
    thinnerTitle: "Dünner Draht (Light Wire)",
    thinnerBadge: "Hohe Durchlässigkeit",
    thinnerDesc: "Ideal für Schwerkraftabscheider, Ansaugfilter, viskose Flüssigkeiten und plissierte Filterelemente mit maximaler Öffnungsfläche.",
    thinnerRow1Label: "Freie Siebfläche (Ao)",
    thinnerRow1Val: "Maximiert (hoher Durchsatz)",
    thinnerRow2Label: "Anfänglicher Differenzdruck (ΔP)",
    thinnerRow2Val: "Minimaler Strömungswiderstand",
    thinnerRow3Label: "Mechanische Eigenstabilität",
    thinnerRow3Val: "Gering (benötigt Stützkörper)",
    thinnerRow4Label: "Kuchenablösung & Rückspülung",
    thinnerRow4Val: "Saubere hydraulische Trennung",
    thinnerCheck: "Häufig kombiniert mit Lochblech-Stützkörpern oder mehrlagiger Sinterung.",
    tableTitle: "4. Referenztabelle für freie Siebflächen von Standardgeweben",
    tableDesc: "Berechnet nach ASTM E2016 für glatte Quadratmaschengewebe: Ao = (w / (w + d))² × 100",
    viewAllCharts: "Alle Diagramme ansehen",
    thMesh: "Maschenzahl (Mesh)",
    thWireDia: "Draht-Ø d (mm)",
    thApertureMm: "Öffnung w (mm)",
    thApertureMicron: "Öffnung (µm)",
    thOpenArea: "Offene Fläche Ao (%)",
    thWeight: "Gewicht (~kg/m²)",
    thApplication: "Typische Anwendung",
    app10: "Grobsiebung, schützende Filterkörbe",
    app20: "Wirbelschichtauflage, petrochemische Siebe",
    app50: "Wasseraufbereitung, Sandabscheidung",
    app100: "Extrusionssiebpakete, Kraftstoffleitungsfilter",
    app200: "Pharmazeutische Pulverklassierung, Hydrauliköl",
    app325: "Ultrafeine chemische Suspensionen, Reinstgasfiltration",
    footerNote: "MutualFilter fertigt Präzisions-Drahtgewebe aus Edelstahl 304, 304L, 316 und 316L. Individuelle Drahtstärken und Kalandrierungen werden gezielt auf Differenzdrücke ausgelegt. Prüfprotokolle und Werkstoffzeugnisse sind auf Bestellung erhältlich.",
    deltaPModels: "Delta-P-Modelle",
    technicalResources: "Technische Ressourcen"
  },
  fr: {
    eyebrow: "Calculs d'ingénierie et normes",
    heading: "Principes fondamentaux du diamètre de fil et de la surface ouverte",
    lead: "Pour les toiles tissées industrielles, le diamètre du fil (d) et l'ouverture (w) déterminent directement le pourcentage de vide de maille (Ao), fixant le compromis entre résistance mécanique, perte de charge et débit.",
    card1Title: "1. Diamètre de fil (d) et métrologie",
    card1Desc: "Étalonnage nominal, compensation de gaufrage et vérification ASTM E2016",
    card1Body: "Le diamètre de fil (d) correspond à l'épaisseur de section nominale des fils de chaîne et de trame avant ou dans la toile. Durant le tissage, le gaufrage aux croisements crée un léger écrasement, nécessitant des méthodes de mesure précises.",
    card1MetrologyHeading: "Méthodes de mesure normalisées",
    card1PointMicrometerTitle: "Micromètre à touches sphériques :",
    card1PointMicrometerDesc: "Mesure sur fil droit non déformé, évitant les imprécisions des ondulations du tissu.",
    card1OpticalTitle: "Projecteur de profil optique :",
    card1OpticalDesc: "Projection numérique sans contact pour toiles fines (≤0.10 mm) prévenant toute déformation.",
    card1LaserTitle: "Métrologie laser en continu :",
    card1LaserDesc: "Surveillance laser en ligne garantissant la conformité des tolérances de fil selon ASTM E2016.",
    card2Title: "2. Surface ouverte (Ao) et calcul de formule",
    card2Desc: "Pourcentage théorique de vide pour toile métallique carrée unie",
    card2Body: "La surface ouverte (Ao) représente la proportion géométrique des ouvertures par rapport à la surface totale de la toile. C'est le paramètre clé déterminant le débit volumique et la perte de charge initiale (ΔP).",
    card2FormulaLabel: "Formule de surface ouverte géométrique (maille carrée)",
    card2VarW: "Largeur d'ouverture (mm)",
    card2VarD: "Diamètre de fil (mm)",
    card2VarPitch: "Pas de tissage (25.4 / Mesh)",
    card2VarAo: "Pourcentage de surface ouverte (%)",
    card2Note: "Remarque : Pour les toiles reps (Dutch weave), les fils sont serrés sans passage en ligne directe ; la finesse est déterminée par point de bulle et seuil absolu en microns.",
    tradeoffTitle: "3. Compromis d'ingénierie : fil épais vs fil fin",
    tradeoffDesc: "Équilibre entre résistance mécanique, capacité de débit et traînée hydraulique",
    tradeoffBadge: "Sélection à ouverture identique",
    thickerTitle: "Fil épais (Heavy Wire)",
    thickerBadge: "Haute rigidité",
    thickerDesc: "Recommandé en cas de fortes pointes de pression différentielle, de boues abrasives, de criblage lourd et de support de toiles frittées.",
    thickerRow1Label: "Résistance mécanique et éclatement",
    thickerRow1Val: "Largement supérieure",
    thickerRow2Label: "Surface ouverte (Ao)",
    thickerRow2Val: "Réduite (débit inférieur)",
    thickerRow3Label: "Perte de charge initiale (ΔP)",
    thickerRow3Val: "Perte par frottement plus élevée",
    thickerRow4Label: "Résistance à l'usure et fatigue",
    thickerRow4Val: "Durée de vie prolongée sous charge",
    thickerAlert: "Nécessite une puissance de pompage accrue pour compenser la perte de charge du passage réduit.",
    thinnerTitle: "Fil fin (Light Wire)",
    thinnerBadge: "Haute perméabilité",
    thinnerDesc: "Idéal pour séparateurs gravitaires, filtres d'admission d'air, fluides visqueux et éléments plissés recherchant une surface libre maximale.",
    thinnerRow1Label: "Surface ouverte (Ao)",
    thinnerRow1Val: "Maximisée (débit élevé)",
    thinnerRow2Label: "Perte de charge initiale (ΔP)",
    thinnerRow2Val: "Résistance minimale",
    thinnerRow3Label: "Autoportance mécanique",
    thinnerRow3Val: "Faible (nécessite un tube support perforé)",
    thinnerRow4Label: "Décollement de gâteau et rétrolavage",
    thinnerRow4Val: "Détachement hydraulique propre",
    thinnerCheck: "Fréquemment combiné avec des manchons perforés de soutien ou un frittage multicouche.",
    tableTitle: "4. Tableau de référence de surface ouverte des toiles standard",
    tableDesc: "Calculé selon la norme ASTM E2016 pour maille carrée unie : Ao = (w / (w + d))² × 100",
    viewAllCharts: "Voir tous les tableaux",
    thMesh: "Nombre de mesh",
    thWireDia: "Diam. fil d (mm)",
    thApertureMm: "Ouverture w (mm)",
    thApertureMicron: "Ouverture (µm)",
    thOpenArea: "Surface ouverte Ao (%)",
    thWeight: "Poids (~kg/m²)",
    thApplication: "Application principale",
    app10: "Débourbage de granulats lourds, paniers de protection grossière",
    app20: "Support de lit fluidisé, crépines pétrochimiques",
    app50: "Clarification d'eau de prise, séparation de sable",
    app100: "Packs de filtres d'extrusion polymère, filtration de carburant",
    app200: "Classification de poudres pharmaceutiques, huile hydraulique",
    app325: "Suspensions chimiques ultrafines, purification de gaz purs",
    footerNote: "MutualFilter fournit des toiles tissées en acier inoxydable 304, 304L, 316 et 316L de précision. Les calibres de fil et toiles calandrées sont configurés sur mesure. Relevés dimensionnels et certificats de matière disponibles sur demande à la commande.",
    deltaPModels: "Modèles Delta P",
    technicalResources: "Ressources techniques"
  },
  pt: {
    eyebrow: "Cálculos de engenharia e normas",
    heading: "Princípios fundamentais de diâmetro de fio e área aberta",
    lead: "Em telas de arame tecido industrial, o diâmetro do fio (d) e a abertura (w) governam a taxa de área aberta (Ao), estabelecendo o equilíbrio entre resistência mecânica, perda de carga e vazão volumétrica.",
    card1Title: "1. Diâmetro do fio (d) e metrologia",
    card1Desc: "Calibração nominal, compensação de ondulação e verificação ASTM E2016",
    card1Body: "O diâmetro do fio (d) é a espessura da seção circular nominal dos fios de urdume e trama antes ou dentro da tela. Durante a tecelagem, a conformação nas interseções gera microdeformações, exigindo técnicas padronizadas de medição.",
    card1MetrologyHeading: "Metrologia padronizada",
    card1PointMicrometerTitle: "Micrômetro de ponta esférica:",
    card1PointMicrometerDesc: "Mede seções retas de fio, evitando distorções nas ondulações da tela.",
    card1OpticalTitle: "Projetor de perfil óptico:",
    card1OpticalDesc: "Projeção digital sem contato para malhas finas (≤0.10 mm) sem deformar a malha.",
    card1LaserTitle: "Metrologia por varredura laser:",
    card1LaserDesc: "Monitoramento contínuo em linha que assegura tolerâncias de arame conforme ASTM E2016.",
    card2Title: "2. Área aberta (Ao) e fórmula de cálculo",
    card2Desc: "Proporção teórica de passagem livre para tela quadrada lisa",
    card2Body: "A área aberta (Ao) representa a proporção geométrica de aberturas em relação à área total da tela. É o fator determinante para a vazão volumétrica e a perda de carga diferencial inicial (ΔP).",
    card2FormulaLabel: "Equação geométrica de área aberta (malha quadrada)",
    card2VarW: "Largura de abertura (mm)",
    card2VarD: "Diâmetro do fio (mm)",
    card2VarPitch: "Passo da trama (25.4 / Mesh)",
    card2VarAo: "Porcentagem de área aberta (%)",
    card2Note: "Nota: Para tecidos holandeses (Dutch weave), os fios adjacentes estão comprimidos sem visão direta de abertura; a retenção é determinada por teste de ponto de bolha e mícrons nominais.",
    tradeoffTitle: "3. Avaliação de engenharia: fio grosso vs. fio fino",
    tradeoffDesc: "Equilíbrio estrutural entre resistência mecânica, vazão e arrasto de fluxo",
    tradeoffBadge: "Seleção para mesma abertura",
    thickerTitle: "Arame grosso (Heavy Wire)",
    thickerBadge: "Alta rigidez",
    thickerDesc: "Recomendado para picos de pressão diferencial, polpas abrasivas, peneiramento vibratório pesado e suporte de telas sinterizadas.",
    thickerRow1Label: "Resistência mecânica e ruptura",
    thickerRow1Val: "Significativamente superior",
    thickerRow2Label: "Porcentagem de área aberta (Ao)",
    thickerRow2Val: "Reduzida (menor vazão)",
    thickerRow3Label: "Perda de carga inicial (ΔP)",
    thickerRow3Val: "Maior atrito hidráulico",
    thickerRow4Label: "Vida útil sob desgaste e fadiga",
    thickerRow4Val: "Ciclo estendido sob carga",
    thickerAlert: "Exige maior potência de bombeamento para compensar a perda de carga do canal reduzido.",
    thinnerTitle: "Arame fino (Light Wire)",
    thinnerBadge: "Alta permeabilidade",
    thinnerDesc: "Ideal para separadores por gravidade, filtros de admissão de ar, despoeiramento de fluidos viscosos e elementos plissados com máxima área livre.",
    thinnerRow1Label: "Porcentagem de área aberta (Ao)",
    thinnerRow1Val: "Maximizada (alta vazão)",
    thinnerRow2Label: "Perda de carga inicial (ΔP)",
    thinnerRow2Val: "Resistência mínima",
    thinnerRow3Label: "Autossustentação mecânica",
    thinnerRow3Val: "Baixa (requer tubo perfurado)",
    thinnerRow4Label: "Desprendimento de torta e retrolavagem",
    thinnerRow4Val: "Separação hidráulica limpa",
    thinnerCheck: "Frequentemente combinado com camisas perfuradas de apoio ou sinterização multicamadas.",
    tableTitle: "4. Tabela de referência de área aberta para telas padrão",
    tableDesc: "Calibrada conforme a norma ASTM E2016 para malha quadrada lisa: Ao = (w / (w + d))² × 100",
    viewAllCharts: "Ver todas as tabelas",
    thMesh: "Contagem de malha",
    thWireDia: "Diâm. fio d (mm)",
    thApertureMm: "Abertura w (mm)",
    thApertureMicron: "Abertura (µm)",
    thOpenArea: "Área aberta Ao (%)",
    thWeight: "Peso (~kg/m²)",
    thApplication: "Aplicação principal",
    app10: "Peneiramento de agregados pesados, cestos de proteção grossa",
    app20: "Suporte de leito fluidizado, filtros petroquímicos",
    app50: "Clarificação de captação de água, separação de areia",
    app100: "Telas de extrusão de polímeros, filtragem de combustível",
    app200: "Classificação de pós farmacêuticos, óleo hidráulico",
    app325: "Suspensões químicas ultrafinas, purificação de gases puros",
    footerNote: "A MutualFilter fornece telas tecidas de aço inoxidável 304, 304L, 316 e 316L de precisão. Calibres de arame e tecidos calandrados são desenvolvidos para perdas de carga específicas. Relatórios de inspeção dimensional e certificados de matéria-prima disponíveis quando solicitados no pedido.",
    deltaPModels: "Modelos Delta P",
    technicalResources: "Recursos técnicos"
  }
};
export default function WireDiameterGuide() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  return <section id="wirediameteropenareaguide" data-nav="light" className="bg-background text-foreground mf-section border-b border-border transition-colors duration-200">
      <div className="mf-container">
        {/* Section Sub-Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md border border-border bg-muted text-foreground mf-caption font-medium tracking-wide uppercase mb-4">
            <Gauge className="size-3.5 text-primary" aria-hidden="true" />
            <span>{copy.eyebrow}</span>
          </div>
          <h2 className="mf-h2 text-foreground">{copy.heading}</h2>
          <p className="mt-4 text-muted-foreground mf-lead">{copy.lead}</p>
        </div>

        {/* Core Principles Grid: Wire Diameter & Formula */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
          {/* 1. Wire Diameter Definition */}
          <div className="lg:col-span-6">
            <Card className="h-full rounded-lg border border-border bg-card">
              <CardHeader>
                <div className="flex items-center justify-between gap-4">
                  <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary shrink-0">
                    <Maximize2 className="size-5" aria-hidden="true" />
                  </div>
                  <Badge variant="outline" className="mf-caption font-mono">
                    d (mm / µm)
                  </Badge>
                </div>
                <CardTitle className="mf-h3 mt-4 text-card-foreground">
                  {copy.card1Title}
                </CardTitle>
                <CardDescription className="mf-caption">
                  {copy.card1Desc}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-card-foreground/90 leading-relaxed">
                <p>{copy.card1Body}</p>

                <div className="rounded-lg bg-muted/60 p-4 border border-border space-y-2.5">
                  <div className="font-medium text-foreground mf-caption uppercase tracking-wider flex items-center gap-1.5">
                    <CircleCheck className="size-3.5 text-primary" aria-hidden="true" />
                    <span>{copy.card1MetrologyHeading}</span>
                  </div>
                  <ul className="space-y-2 mf-caption text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="font-semibold text-foreground shrink-0">•</span>
                      <span>
                        <strong className="text-foreground">
                          {copy.card1PointMicrometerTitle}
                        </strong>{" "}
                        {copy.card1PointMicrometerDesc}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-semibold text-foreground shrink-0">•</span>
                      <span>
                        <strong className="text-foreground">
                          {copy.card1OpticalTitle}
                        </strong>{" "}
                        {copy.card1OpticalDesc}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-semibold text-foreground shrink-0">•</span>
                      <span>
                        <strong className="text-foreground">
                          {copy.card1LaserTitle}
                        </strong>{" "}
                        {copy.card1LaserDesc}
                      </span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* 2. Open Area Formula */}
          <div className="lg:col-span-6">
            <Card className="h-full rounded-lg border border-border bg-card">
              <CardHeader>
                <div className="flex items-center justify-between gap-4">
                  <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary shrink-0">
                    <Calculator className="size-5" aria-hidden="true" />
                  </div>
                  <Badge variant="outline" className="mf-caption font-mono">
                    {"Ao (%)"}
                  </Badge>
                </div>
                <CardTitle className="mf-h3 mt-4 text-card-foreground">
                  {copy.card2Title}
                </CardTitle>
                <CardDescription className="mf-caption">
                  {copy.card2Desc}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-card-foreground/90 leading-relaxed">
                <p>{copy.card2Body}</p>

                {/* Mathematical Highlight Box */}
                <div className="rounded-lg bg-muted/60 p-4 border border-border">
                  <div className="mf-caption font-semibold text-foreground uppercase tracking-wider mb-2">
                    {copy.card2FormulaLabel}
                  </div>
                  <div className="font-mono text-base md:text-lg text-primary font-bold bg-background px-3 py-2.5 rounded border border-border inline-block">
                    {"Ao = (w / (w + d))² × 100%"}
                  </div>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 mf-caption text-muted-foreground pt-1">
                    <div>
                      <span className="font-semibold text-foreground">w</span> ={" "}
                      {copy.card2VarW}
                    </div>
                    <div>
                      <span className="font-semibold text-foreground">d</span> ={" "}
                      {copy.card2VarD}
                    </div>
                    <div>
                      <span className="font-semibold text-foreground">w + d</span> ={" "}
                      {copy.card2VarPitch}
                    </div>
                    <div>
                      <span className="font-semibold text-foreground">Ao</span> ={" "}
                      {copy.card2VarAo}
                    </div>
                  </div>
                </div>

                <p className="mf-caption text-muted-foreground">{copy.card2Note}</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* 3. Engineering Trade-off Matrix */}
        <div className="mb-12">
          <Card className="rounded-lg border border-border bg-card overflow-hidden">
            <CardHeader className="border-b border-border bg-muted/20">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary shrink-0">
                    <SlidersHorizontal className="size-5" aria-hidden="true" />
                  </div>
                  <div>
                    <CardTitle className="mf-h3 text-card-foreground">
                      {copy.tradeoffTitle}
                    </CardTitle>
                    <CardDescription className="mf-caption">
                      {copy.tradeoffDesc}
                    </CardDescription>
                  </div>
                </div>
                <Badge variant="outline" className="w-fit mf-caption">
                  {copy.tradeoffBadge}
                </Badge>
              </div>
            </CardHeader>

            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Thicker Wire Panel */}
                <div className="rounded-lg border border-border p-5 bg-background flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="mf-h3 text-foreground flex items-center gap-2">
                        <span className="size-2 rounded-full bg-chart-1" />
                        {copy.thickerTitle}
                      </h3>
                      <Badge variant="outline" className="mf-caption font-mono">
                        {copy.thickerBadge}
                      </Badge>
                    </div>
                    <p className="mf-caption text-muted-foreground mb-4">
                      {copy.thickerDesc}
                    </p>

                    <div className="space-y-2.5 mf-caption">
                      <div className="flex justify-between py-1.5 border-b border-border">
                        <span className="text-muted-foreground">
                          {copy.thickerRow1Label}
                        </span>
                        <span className="font-medium text-foreground">
                          {copy.thickerRow1Val}
                        </span>
                      </div>
                      <div className="flex justify-between py-1.5 border-b border-border">
                        <span className="text-muted-foreground">
                          {copy.thickerRow2Label}
                        </span>
                        <span className="font-medium text-destructive">
                          {copy.thickerRow2Val}
                        </span>
                      </div>
                      <div className="flex justify-between py-1.5 border-b border-border">
                        <span className="text-muted-foreground">
                          {copy.thickerRow3Label}
                        </span>
                        <span className="font-medium text-foreground">
                          {copy.thickerRow3Val}
                        </span>
                      </div>
                      <div className="flex justify-between py-1.5">
                        <span className="text-muted-foreground">
                          {copy.thickerRow4Label}
                        </span>
                        <span className="font-medium text-foreground">
                          {copy.thickerRow4Val}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 pt-3 border-t border-border flex items-center gap-2 mf-caption text-muted-foreground">
                    <ShieldAlert className="size-4 text-primary shrink-0" aria-hidden="true" />
                    <span>{copy.thickerAlert}</span>
                  </div>
                </div>

                {/* Thinner Wire Panel */}
                <div className="rounded-lg border border-border p-5 bg-background flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="mf-h3 text-foreground flex items-center gap-2">
                        <span className="size-2 rounded-full bg-chart-4" />
                        {copy.thinnerTitle}
                      </h3>
                      <Badge variant="outline" className="mf-caption font-mono">
                        {copy.thinnerBadge}
                      </Badge>
                    </div>
                    <p className="mf-caption text-muted-foreground mb-4">
                      {copy.thinnerDesc}
                    </p>

                    <div className="space-y-2.5 mf-caption">
                      <div className="flex justify-between py-1.5 border-b border-border">
                        <span className="text-muted-foreground">
                          {copy.thinnerRow1Label}
                        </span>
                        <span className="font-medium text-primary">
                          {copy.thinnerRow1Val}
                        </span>
                      </div>
                      <div className="flex justify-between py-1.5 border-b border-border">
                        <span className="text-muted-foreground">
                          {copy.thinnerRow2Label}
                        </span>
                        <span className="font-medium text-foreground">
                          {copy.thinnerRow2Val}
                        </span>
                      </div>
                      <div className="flex justify-between py-1.5 border-b border-border">
                        <span className="text-muted-foreground">
                          {copy.thinnerRow3Label}
                        </span>
                        <span className="font-medium text-destructive">
                          {copy.thinnerRow3Val}
                        </span>
                      </div>
                      <div className="flex justify-between py-1.5">
                        <span className="text-muted-foreground">
                          {copy.thinnerRow4Label}
                        </span>
                        <span className="font-medium text-foreground">
                          {copy.thinnerRow4Val}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 pt-3 border-t border-border flex items-center gap-2 mf-caption text-muted-foreground">
                    <CircleCheck className="size-4 text-primary shrink-0" aria-hidden="true" />
                    <span>{copy.thinnerCheck}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 4. Reference Engineering Table */}
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h3 className="mf-h3 text-foreground flex items-center gap-2">
                <TableIcon className="size-5 text-primary" aria-hidden="true" />
                <span>{copy.tableTitle}</span>
              </h3>
              <p className="mf-caption text-muted-foreground mt-1">
                {copy.tableDesc}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button asChild variant="outline" size="sm">
                <Link to="/TechnicalChartsPage" className="inline-flex items-center gap-1.5">
                  <span>{copy.viewAllCharts}</span>
                  <ArrowRight className="size-3.5" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </div>

          <Card className="rounded-lg border border-border overflow-hidden bg-card">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-muted/60">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-24 font-semibold text-foreground">
                      {copy.thMesh}
                    </TableHead>
                    <TableHead className="font-semibold text-foreground">
                      {copy.thWireDia}
                    </TableHead>
                    <TableHead className="font-semibold text-foreground">
                      {copy.thApertureMm}
                    </TableHead>
                    <TableHead className="font-semibold text-foreground">
                      {copy.thApertureMicron}
                    </TableHead>
                    <TableHead className="font-semibold text-foreground">
                      {copy.thOpenArea}
                    </TableHead>
                    <TableHead className="font-semibold text-foreground">
                      {copy.thWeight}
                    </TableHead>
                    <TableHead className="text-right font-semibold text-foreground">
                      {copy.thApplication}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {REFERENCE_DATA.map((row, idx) => {
                  let appText = "";
                  switch (row.mesh) {
                    case 10:
                      appText = copy.app10;
                      break;
                    case 20:
                      appText = copy.app20;
                      break;
                    case 50:
                      appText = copy.app50;
                      break;
                    case 100:
                      appText = copy.app100;
                      break;
                    case 200:
                      appText = copy.app200;
                      break;
                    case 325:
                      appText = copy.app325;
                      break;
                    default:
                      appText = copy.app10;
                  }
                  return <TableRow key={row.mesh} data-index={idx} className="hover:bg-muted/40 transition-colors">
                        <TableCell className="font-mono font-bold text-foreground">
                          {row.mesh} #
                        </TableCell>
                        <TableCell className="font-mono text-muted-foreground">
                          {row.wireDiameterMm.toFixed(3)}
                        </TableCell>
                        <TableCell className="font-mono text-muted-foreground">
                          {row.apertureMm.toFixed(3)}
                        </TableCell>
                        <TableCell className="font-mono text-foreground font-medium">
                          {row.apertureMicron}
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center px-2 py-0.5 rounded border border-border bg-muted text-primary font-mono font-bold mf-caption">
                            {row.openAreaPct.toFixed(1)}%
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-muted-foreground">
                          {row.nominalWeightKgM2.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right mf-caption text-muted-foreground">
                          {appText}
                        </TableCell>
                      </TableRow>;
                })}
                </TableBody>
              </Table>
            </div>
          </Card>
        </div>

        {/* Footer Technical Note & Related Links */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="mf-caption text-muted-foreground max-w-2xl">
            <p>{copy.footerNote}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Button asChild variant="outline" size="sm">
              <Link to="/CalculationGuidesPage" className="inline-flex items-center gap-1.5">
                <span>{copy.deltaPModels}</span>
                <ArrowRight className="size-3.5" aria-hidden="true" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/TechnicalResourcesPage" className="inline-flex items-center gap-1.5">
                <span>{copy.technicalResources}</span>
                <ArrowRight className="size-3.5" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}